import { BE1_API } from '../config/api';
interface Column {
  name: string;
  comment: string;
  dataType: string;
}

interface TableColumnsResponse {
  columns: Column[];
}

// Mock data for table columns
const MOCK_TABLE_COLUMNS: Record<string, TableColumnsResponse> = {
  'production': {
    columns: [
      { name: 'id', comment: '제품 ID', dataType: 'INTEGER' },
      { name: 'product_name', comment: '제품명', dataType: 'VARCHAR(200)' },
      { name: 'category', comment: '카테고리', dataType: 'VARCHAR(100)' },
      { name: 'price', comment: '가격', dataType: 'DECIMAL(10,2)' },
      { name: 'created_at', comment: '생성일시', dataType: 'TIMESTAMP' }
    ]
  },
  'sales': {
    columns: [
      { name: 'id', comment: '판매 ID', dataType: 'INTEGER' },
      { name: 'product_id', comment: '제품 ID', dataType: 'INTEGER' },
      { name: 'quantity', comment: '수량', dataType: 'INTEGER' },
      { name: 'amount', comment: '금액', dataType: 'DECIMAL(12,2)' },
      { name: 'sale_date', comment: '판매일자', dataType: 'DATE' }
    ]
  },
  'users': {
    columns: [
      { name: 'id', comment: '사용자 ID', dataType: 'INTEGER' },
      { name: 'username', comment: '사용자명', dataType: 'VARCHAR(50)' },
      { name: 'email', comment: '이메일', dataType: 'VARCHAR(100)' },
      { name: 'created_at', comment: '가입일시', dataType: 'TIMESTAMP' },
      { name: 'status', comment: '상태', dataType: 'VARCHAR(20)' }
    ]
  },
  'logs': {
    columns: [
      { name: 'id', comment: '로그 ID', dataType: 'INTEGER' },
      { name: 'user_id', comment: '사용자 ID', dataType: 'INTEGER' },
      { name: 'activity', comment: '활동내역', dataType: 'TEXT' },
      { name: 'ip_address', comment: 'IP주소', dataType: 'VARCHAR(15)' },
      { name: 'timestamp', comment: '발생시간', dataType: 'TIMESTAMP' }
    ]
  },
  'test_table': {
    columns: [
      { name: 'id', comment: '테스트 ID', dataType: 'INTEGER' },
      { name: 'test_col1', comment: '테스트 컬럼1', dataType: 'VARCHAR(100)' },
      { name: 'test_col2', comment: '테스트 컬럼2', dataType: 'VARCHAR(100)' },
      { name: 'created_at', comment: '생성일시', dataType: 'TIMESTAMP' },
      { name: 'test_col3', comment: '테스트 컬럼3', dataType: 'VARCHAR(100)' },
      { name: 'test_col4', comment: '테스트 컬럼4', dataType: 'VARCHAR(100)' },
      { name: 'test_col5', comment: '테스트 컬럼5', dataType: 'VARCHAR(100)' }
    ]
  }
};

const BASE_API_URL = BE1_API;

/**
 * Fetches columns for a specific table from a database connection
 * @param connection Database connection name
 * @param tableName Table name to fetch columns for
 * @returns Promise resolving to table columns information
 */
export const fetchTableColumns = async (
  connection: string,
  tableName: string,
  schema?: string
): Promise<TableColumnsResponse> => {
  // 커넥션(접속 계정)에는 여러 스키마가 보이므로 스키마를 함께 넘긴다 (없으면 커넥션 기본 스키마)
  const qs = schema ? `?schema=${encodeURIComponent(schema)}` : '';
  const url = `${BASE_API_URL}/api/connections/${encodeURIComponent(connection)}/tables/${encodeURIComponent(tableName)}/columns/${qs}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch columns: ${response.statusText}`);
  }
  // 서버는 자료형을 `type` 으로 준다 — 화면은 `dataType` 으로 쓴다. 예전에는 그대로 넘겨 DB테이블 노드의
  // 자료형이 전부 비었고, LOB 항목도 찾지 못했다(현장 2026-10-30).
  const body = await response.json();
  return {
    ...body,
    columns: (body?.columns || []).map((c: any) => ({ ...c, dataType: c.dataType ?? c.type ?? '' })),
  };
};

export type { Column, TableColumnsResponse }; 